import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowRight, Menu, X, Briefcase, Megaphone, Users, Music, Trophy, Wifi, Sparkles } from "lucide-react";
import { SOLUTIONS, SERVICES, EXPLORE, EXPERIENCES, ABOUT } from "../data/navigation";
import { WhatsAppIcon } from "./WhatsAppIcon";

const PILLAR_ICONS = { business: Briefcase, impact: Megaphone, people: Users, live: Music, play: Trophy, connect: Wifi };

const SolutionsMega = () => (
  <div data-testid="mega-menu-panel" className="ev-mega-panel absolute left-0 right-0 top-full mt-3 bg-white rounded-2xl border border-slate-100 shadow-[0_30px_60px_-20px_rgba(0,20,45,0.25)]">
    <div className="max-w-[1400px] mx-auto px-8 py-7 max-h-[78vh] overflow-y-auto">
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-3 grid grid-cols-3 gap-4">
          {SOLUTIONS.map((s, i) => {
            const Icon = PILLAR_ICONS[s.id];
            return (
              <div key={s.id} style={{ animationDelay: `${i * 45}ms` }} className="ev-mega-panel rounded-2xl bg-[#F5F8FA] border border-transparent hover:border-[#32B5E8]/30 p-4 transition-colors duration-300">
                <Link to={`/solutions#${s.id}`} className="flex items-center gap-2.5 mb-2 group">
                  <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1679B5] to-[#32B5E8] flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-white" />
                  </span>
                  <span className="text-[13px] font-extrabold tracking-wide uppercase text-[#081D35] group-hover:text-[#1679B5] transition-colors">{s.title}</span>
                </Link>
                <ul>
                  {s.items.map((item) => (
                    <li key={item}>
                      <Link to={`/solutions#${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group/item flex items-center justify-between px-2 py-[5px] rounded-lg text-[13px] text-[#475569] hover:bg-white hover:text-[#1679B5] transition-colors">
                        {item}
                        <ChevronRight size={12} className="opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-200 text-[#32B5E8]" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col gap-4">
          <p className="text-[11px] font-extrabold tracking-[0.18em] uppercase text-[#64748B] px-1">Featured</p>
          <Link to="/solutions#connect" style={{ animationDelay: "120ms" }} className="ev-mega-panel group rounded-2xl bg-[#EAF4FB] border border-[#32B5E8]/20 p-5 hover:-translate-y-0.5 hover:shadow-[0_15px_30px_-15px_rgba(22,121,181,0.4)] transition-all duration-300">
            <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-3 shadow-sm">
              <Wifi size={18} className="text-[#1679B5]" />
            </span>
            <p className="text-base font-extrabold text-[#081D35]">Evontra Connect</p>
            <p className="text-xs text-[#64748B] mt-1 leading-relaxed">Hybrid, virtual & event technology experiences</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#1679B5]">Explore Now <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-1" /></span>
          </Link>
          <Link to="/solutions#live" style={{ animationDelay: "180ms" }} className="ev-mega-panel group rounded-2xl bg-[#F1F8E7] border border-[#8BCB32]/25 p-5 hover:-translate-y-0.5 hover:shadow-[0_15px_30px_-15px_rgba(111,175,45,0.4)] transition-all duration-300">
            <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-3 shadow-sm">
              <Music size={18} className="text-[#6FAF2D]" />
            </span>
            <p className="text-base font-extrabold text-[#081D35]">Evontra Live</p>
            <p className="text-xs text-[#64748B] mt-1 leading-relaxed">Concerts, stage productions & artist experiences</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#6FAF2D]">Explore Now <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-1" /></span>
          </Link>
          <Link to="/plan-your-event" style={{ animationDelay: "240ms" }} className="ev-mega-panel group rounded-2xl bg-gradient-to-br from-[#081D35] to-[#1679B5] p-5 hover:-translate-y-0.5 hover:shadow-[0_18px_35px_-15px_rgba(8,29,53,0.6)] transition-all duration-300 mt-auto">
            <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center mb-3">
              <Sparkles size={18} className="text-[#32B5E8]" />
            </span>
            <p className="text-base font-extrabold text-white">Plan Your Event</p>
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">Share your brief — we'll craft the concept</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#32B5E8]">Start Now <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-1" /></span>
          </Link>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-slate-100 flex justify-end">
        <Link data-testid="mega-menu-explore-all" to="/solutions" className="group inline-flex items-center gap-2 text-sm font-bold text-[#1679B5] hover:text-[#081D35] transition-colors">
          Explore All Solutions
          <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  </div>
);

const MegaPanel = ({ columns, footerLink, footerText, colsClass }) => (
  <div data-testid="mega-menu-panel" className="ev-mega-panel absolute left-0 right-0 top-full mt-3 bg-white rounded-2xl border border-slate-100 shadow-[0_30px_60px_-20px_rgba(0,20,45,0.25)]">
    <div className="max-w-7xl mx-auto px-8 py-10">
      <div className={`grid ${colsClass} gap-10`}>
        {columns.map((col) => (
          <div key={col.title}>
            <p className="text-[11px] font-extrabold tracking-[0.18em] uppercase text-[#1679B5] mb-1">{col.title}</p>
            {col.tagline && <p className="text-xs text-[#64748B] mb-4">{col.tagline}</p>}
            {!col.tagline && <div className="mb-4" />}
            <ul className="space-y-2">
              {col.items.map((item) => (
                <li key={item}>
                  <Link to={`/solutions#${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm text-[#081D35] hover:text-[#1679B5] hover:translate-x-1 inline-block transition-all duration-200">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {footerLink && (
        <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
          <Link data-testid="mega-menu-explore-all" to={footerLink} className="group inline-flex items-center gap-2 text-sm font-bold text-[#1679B5] hover:text-[#081D35] transition-colors">
            {footerText}
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      )}
    </div>
  </div>
);

const DropPanel = ({ items, base }) => (
  <div data-testid="dropdown-panel" className="ev-mega-panel absolute left-1/2 -translate-x-1/2 top-full pt-1">
    <div className="w-72 bg-white rounded-xl border border-slate-100 shadow-[0_25px_50px_-15px_rgba(0,20,45,0.25)] p-3">
      {items.map((item) => (
        <Link key={item} to={`${base}#${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium text-[#081D35] hover:bg-[#F5F8FA] hover:text-[#1679B5] transition-colors">
          {item}
          <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 text-[#32B5E8]" />
        </Link>
      ))}
    </div>
  </div>
);

const NavItem = ({ label, to, testId, fullWidth, children }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const enter = () => { clearTimeout(timer.current); setOpen(true); };
  const leave = () => { timer.current = setTimeout(() => setOpen(false), 140); };
  return (
    <div className={fullWidth ? "" : "relative"} onMouseEnter={enter} onMouseLeave={leave}>
      <Link data-testid={testId} to={to} className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold transition-colors ${open ? "text-[#1679B5]" : "text-[#081D35] hover:text-[#1679B5]"}`}>
        {label}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180 text-[#32B5E8]" : "text-[#64748B]"}`} />
      </Link>
      {open && children}
    </div>
  );
};

const MobileSection = ({ label, to, groups, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="border-b border-slate-100">
      <div className="flex items-center">
        <button data-testid={`mobile-nav-${label.toLowerCase()}`} onClick={() => { onNavigate(); navigate(to); }} className="flex-1 text-left px-5 py-4 text-base font-bold text-[#081D35]">
          {label}
        </button>
        <button data-testid={`mobile-nav-${label.toLowerCase()}-toggle`} aria-label={`Expand ${label}`} onClick={() => setOpen(!open)} className="px-5 py-4">
          <ChevronDown size={18} className={`text-[#1679B5] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="pb-4 px-5">
          {groups.map((g) => (
            <div key={g.title} className="mb-4">
              <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#1679B5] mb-2">{g.title}</p>
              <div className="flex flex-wrap gap-2">
                {g.items.map((item) => (
                  <Link key={item} onClick={onNavigate} to={`${to}#${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#F5F8FA] text-[#081D35] border border-slate-200">
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setMobileOpen(false);

  return (
    <header data-testid="site-header" className="sticky top-3 z-50 px-3 sm:px-5">
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 rounded-2xl bg-white/90 backdrop-blur-md border border-slate-200/70 shadow-[0_15px_40px_-15px_rgba(8,29,53,0.25)]">
        <div className="flex items-center justify-between h-[72px]">
          <Link data-testid="nav-logo" to="/" onClick={close} className="flex items-center shrink-0">
            <img src="/evontra-logo.png" alt="Evontra" className="h-10 w-auto" />
          </Link>

          <nav data-testid="desktop-nav" className="hidden xl:flex items-center gap-1">
            <Link data-testid="nav-home" to="/" className="px-3 py-2 text-sm font-semibold text-[#081D35] hover:text-[#1679B5] transition-colors">Home</Link>
            <NavItem label="Solutions" to="/solutions" testId="nav-solutions" fullWidth>
              <SolutionsMega />
            </NavItem>
            <NavItem label="Services" to="/services" testId="nav-services">
              <DropPanel items={SERVICES} base="/services" />
            </NavItem>
            <NavItem label="Explore" to="/explore" testId="nav-explore" fullWidth>
              <MegaPanel columns={EXPLORE} colsClass="grid-cols-4" />
            </NavItem>
            <NavItem label="Experiences" to="/experiences" testId="nav-experiences">
              <DropPanel items={EXPERIENCES} base="/experiences" />
            </NavItem>
            <NavItem label="About" to="/about" testId="nav-about">
              <DropPanel items={ABOUT} base="/about" />
            </NavItem>
            <Link data-testid="nav-contact" to="/contact" className="px-3 py-2 text-sm font-semibold text-[#081D35] hover:text-[#1679B5] transition-colors">Contact</Link>
          </nav>

          <div className="flex items-center gap-3">
            <a data-testid="nav-whatsapp-cta" href="https://wa.me/919836372200" target="_blank" rel="noopener noreferrer" className="ev-whatsapp-btn hidden sm:inline-flex items-center gap-2 bg-white border border-slate-200 text-[#081D35] text-sm font-bold px-5 py-2.5 rounded-full">
              <WhatsAppIcon size={17} /> WhatsApp
            </a>
            <Link data-testid="nav-plan-event-cta" to="/plan-your-event" className="ev-btn-primary hidden sm:inline-flex items-center gap-2 text-white text-sm font-bold px-5 py-2.5 rounded-full">
              Plan Your Event <ArrowRight size={15} />
            </Link>
            <button data-testid="mobile-menu-button" aria-label="Toggle menu" onClick={() => setMobileOpen(!mobileOpen)} className="xl:hidden p-2 text-[#081D35]">
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-nav-panel" className="xl:hidden mt-3 rounded-2xl bg-white border border-slate-200/70 shadow-[0_25px_50px_-20px_rgba(8,29,53,0.3)] max-h-[calc(100vh-100px)] overflow-y-auto">
          <Link data-testid="mobile-nav-home" to="/" onClick={close} className="block px-5 py-4 text-base font-bold text-[#081D35] border-b border-slate-100">Home</Link>
          <MobileSection label="Solutions" to="/solutions" groups={SOLUTIONS} onNavigate={close} />
          <MobileSection label="Services" to="/services" groups={[{ title: "Services", items: SERVICES }]} onNavigate={close} />
          <MobileSection label="Explore" to="/explore" groups={EXPLORE} onNavigate={close} />
          <MobileSection label="Experiences" to="/experiences" groups={[{ title: "Experiences", items: EXPERIENCES }]} onNavigate={close} />
          <MobileSection label="About" to="/about" groups={[{ title: "About", items: ABOUT }]} onNavigate={close} />
          <Link data-testid="mobile-nav-contact" to="/contact" onClick={close} className="block px-5 py-4 text-base font-bold text-[#081D35] border-b border-slate-100">Contact</Link>
          <div className="p-5">
            <Link data-testid="mobile-plan-event-cta" to="/plan-your-event" onClick={close} className="ev-btn-primary flex items-center justify-center gap-2 text-white text-sm font-bold px-5 py-3 rounded-full">
              Plan Your Event <ArrowRight size={15} />
            </Link>
            <a data-testid="mobile-whatsapp-cta" href="https://wa.me/919836372200" target="_blank" rel="noopener noreferrer" className="ev-whatsapp-btn mt-3 flex items-center justify-center gap-2 bg-white border border-slate-200 text-[#081D35] text-sm font-bold px-5 py-3 rounded-full">
              <WhatsAppIcon size={17} /> WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
